import { useState } from "react";
import { ApolloError } from "@apollo/client";
import { Select } from "@normasteaching/norma-ui";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderOpenIcon,
} from "@heroicons/react/24/outline/index.js";
import { Card } from "../../UI/Card.tsx";
import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { TextInput } from "../../UI/TextInput.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { TextButton } from "../../UI/TextButton.tsx";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { useConfirm } from "../../UI/Feedback/useConfirm.ts";
import { useToast } from "../../UI/Feedback/useToast.ts";
import { useAllUserGroups } from "../../UserGroup/useAllUserGroups.ts";
import { useOnboardingLivePdfs } from "./useOnboardingLivePdfs.ts";

type EditingPdf = { userGroupId?: string; pdfUrl: string; isNew: boolean };

export function OnboardingLivePdfSection() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EditingPdf>();
  const { pdfs, loading, saving, upsertPdf, deletePdf } =
    useOnboardingLivePdfs();
  const { userGroups } = useAllUserGroups();
  const askConfirmation = useConfirm();
  const showToast = useToast();
  const groupsWithOnboardingLive = userGroups.filter(
    (g) => g.onboardingLiveRequired,
  );
  const groupsWithoutPdf = groupsWithOnboardingLive.filter(
    (g) => !pdfs.some((p) => p.userGroupId === g.id),
  );
  const groupNameOf = (userGroupId: string) =>
    userGroups.find((g) => g.id === userGroupId)?.name ?? userGroupId;

  async function save() {
    if (!editing?.userGroupId || !editing.pdfUrl.trim()) {
      showToast("Seleziona uno user group e inserisci l'url del pdf", "error");
      return;
    }
    try {
      await upsertPdf(editing.userGroupId, editing.pdfUrl.trim());
      setEditing(undefined);
      showToast("PDF onboarding salvato");
    } catch (e) {
      showToast((e as ApolloError)?.message ?? "Errore", "error");
    }
  }

  async function remove(userGroupId: string) {
    const confirmed = await askConfirmation({
      title: `Eliminare il PDF onboarding di ${groupNameOf(userGroupId)}?`,
      message:
        "Senza PDF non sarà possibile creare nuove live onboarding per questo gruppo.",
      confirmLabel: "Elimina",
      isDestructive: true,
    });
    if (!confirmed) return;
    await deletePdf(userGroupId);
  }

  return (
    <Card>
      <div className={"flex items-center justify-between gap-3"}>
        <button
          onClick={() => setOpen((v) => !v)}
          className={
            "flex items-center gap-2 text-left hover:text-slate-600 transition-colors"
          }
        >
          {open ? (
            <FolderOpenIcon className={"w-5 h-5 text-slate-500"} />
          ) : (
            <FolderIcon className={"w-5 h-5 text-slate-500"} />
          )}
          <span className={"font-bold text-xl"}>Onboarding Live PDFs</span>
          {open ? (
            <ChevronDownIcon className={"w-4 h-4 text-slate-400"} />
          ) : (
            <ChevronRightIcon className={"w-4 h-4 text-slate-400"} />
          )}
        </button>
        {groupsWithoutPdf.length > 0 ? (
          <TextButton
            label={"+ Nuovo PDF onboarding"}
            onClick={() => {
              setOpen(true);
              setEditing({ isNew: true, pdfUrl: "" });
            }}
          />
        ) : null}
      </div>
      {open ? (
        <div className={"mt-4 space-y-4"}>
          {groupsWithOnboardingLive.length <= 0 ? (
            <p className={"text-sm text-slate-500"}>
              Nessuno user group prevede le live onboarding. Abilitale dalla
              pagina User Groups.
            </p>
          ) : null}
          {editing ? (
            <div className={"rounded border p-3 space-y-3"}>
              <InputWithLabel label={"User group"}>
                {editing.isNew ? (
                  <Select<string>
                    placeholder={"Seleziona uno user group"}
                    options={groupsWithoutPdf.map((g) => ({
                      id: g.id,
                      name: g.name,
                    }))}
                    value={editing.userGroupId}
                    onChange={(v) =>
                      setEditing({ ...editing, userGroupId: v ?? undefined })
                    }
                  />
                ) : (
                  <TextInput
                    readOnly
                    value={groupNameOf(editing.userGroupId ?? "")}
                  />
                )}
              </InputWithLabel>
              <InputWithLabel label={"Url del PDF"}>
                <TextInput
                  value={editing.pdfUrl}
                  placeholder={"https://..."}
                  onChange={(e) =>
                    setEditing({ ...editing, pdfUrl: e.target.value })
                  }
                />
              </InputWithLabel>
              <div className={"flex justify-end gap-2"}>
                <TextButton
                  label={"Annulla"}
                  onClick={() => setEditing(undefined)}
                />
                <PrimaryButton
                  label={"Salva"}
                  loading={saving}
                  onClick={save}
                />
              </div>
            </div>
          ) : null}
          {loading ? (
            <LoadingRows />
          ) : (
            <div className={"divide-y"}>
              {pdfs.length <= 0 ? (
                <p className={"text-sm text-slate-500 py-2"}>
                  Nessun PDF onboarding configurato.
                </p>
              ) : (
                pdfs.map((pdf) => (
                  <div
                    key={pdf.userGroupId}
                    className={"py-3 flex items-center justify-between gap-3"}
                  >
                    <div className={"min-w-0"}>
                      <p className={"font-medium"}>
                        {groupNameOf(pdf.userGroupId)}
                      </p>
                      <a
                        className={"text-sm text-blue-600 underline break-all"}
                        href={pdf.pdfUrl}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                      >
                        {pdf.pdfUrl}
                      </a>
                    </div>
                    <div className={"flex gap-2 shrink-0"}>
                      <TextButton
                        label={"Modifica"}
                        onClick={() =>
                          setEditing({
                            isNew: false,
                            userGroupId: pdf.userGroupId,
                            pdfUrl: pdf.pdfUrl,
                          })
                        }
                      />
                      <TextButton
                        label={"Elimina"}
                        onClick={() => remove(pdf.userGroupId)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}
